import type { WorkspaceDocumentKind, WorkspaceStep } from '../workspace.types';

/**
 * Sample drafts for the dev / quick-start experience — mirrors the shape a live
 * agent would stream over `open_workspace`. NOT shipped product copy; it lets
 * the workspace's draft → edit → create flow be exercised without a backend.
 */
export interface SampleDocument {
  documentKind: WorkspaceDocumentKind;
  title: string;
  subtitle: string;
  steps: Array<Pick<WorkspaceStep, 'id' | 'label'>>;
  body: string;
}

export const SAMPLE_DOCUMENTS: Record<
  'referral-letter' | 'email' | 'session-document',
  SampleDocument
> = {
  'referral-letter': {
    documentKind: 'referral-letter',
    title: 'Referral to Dr Nguyen — Endocrinology',
    subtitle: "For Priya Nair · drafted from today's session",
    steps: [
      { id: 'context', label: 'Reading patient history' },
      { id: 'draft', label: 'Writing referral' },
      { id: 'format', label: 'Formatting letter' },
    ],
    body: `Dear Dr Nguyen,

Thank you for seeing Priya Nair, a 47-year-old woman, regarding her recently identified low iron levels and associated fatigue.

Her ferritin returned at 11 µg/L on Tuesday's bloods, with haemoglobin at the lower end of normal. She reports three months of tiredness and reduced exercise tolerance, without overt bleeding. Menstrual history and dietary intake are being reviewed.

I would be grateful for your assessment and advice on further investigation and management of her iron deficiency, including consideration of an underlying cause.

I have attached the relevant results. Please don't hesitate to contact me if any further information would be helpful.

Kind regards,
Dr Mai Nguyen
Riverside Medical Centre`,
  },
  email: {
    documentKind: 'email',
    title: 'Email to Priya Nair — results follow-up',
    subtitle: 'Patient-friendly tone · drafted from today’s session',
    steps: [
      { id: 'context', label: 'Reading results' },
      { id: 'draft', label: 'Writing email' },
    ],
    body: `Hi Priya,

Thanks for coming in. Your results from Tuesday have come back and I've had a chance to review them.

Most values are within the normal range, but your iron levels are a little lower than we'd like. This is usually straightforward to address, and I'd like to discuss it with you at a short follow-up.

Please book a standard consultation in the next two weeks — online at the clinic booking link or by calling the practice. There's nothing urgent here, so no need to worry in the meantime.

Kind regards,
Dr Mai Nguyen
Riverside Medical Centre`,
  },
  'session-document': {
    documentKind: 'session-document',
    title: 'Consult note — Priya Nair',
    subtitle: 'SOAP · drafted from today’s session',
    steps: [
      { id: 'transcript', label: 'Reading transcript' },
      { id: 'draft', label: 'Writing note' },
      { id: 'structure', label: 'Structuring sections' },
    ],
    body: `Subjective
47-year-old woman presenting with three months of fatigue and reduced exercise tolerance. No overt bleeding. No chest pain, breathlessness, or dizziness at rest.

Objective
Observations within normal limits. Ferritin 11 µg/L; haemoglobin at the lower end of normal.

Assessment
Iron deficiency, likely contributing to fatigue. Underlying cause to be determined.

Plan
- Referral to endocrinology for assessment.
- Review dietary intake and menstrual history.
- Follow-up consultation within two weeks to discuss results.`,
  },
};
